/**
 * Somebody signs for the load.
 *
 * A finger on a phone screen, drawn into a canvas. Deliberately plain: no smoothing
 * cleverness, no pressure, no undo. A signature on a delivery note is a mark somebody
 * made in front of the driver, not a drawing — the only things that matter are that it
 * captures what the finger did and that "Clear" is obvious when the first attempt goes
 * wrong on a bumpy tailgate.
 *
 * Kept small (a few kilobytes of PNG) so it goes up on a bad line, and white-backed
 * rather than transparent, because it will be shown on screens we do not control and
 * a transparent signature on a dark background is invisible.
 */
import { useEffect, useRef, useState } from 'react'

/** Enough to read the mark back; small enough to send from a lorry. */
const WIDTH = 640
const HEIGHT = 200

interface Props {
  /** Called with the finished mark, or null when it is cleared. */
  onChange: (blob: Blob | null) => void
  label?: string
  disabled?: boolean
}

export function SignaturePad({ onChange, label = 'Sign here', disabled }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const drew = useRef(false)
  const [hasMark, setHasMark] = useState(false)

  // The canvas is a fixed drawing surface stretched by CSS to whatever width the phone
  // has. Pointer coordinates therefore need scaling back, or the line lands nowhere
  // near the finger on a narrow screen.
  function pointFrom(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    }
  }

  function paintBackground() {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, WIDTH, HEIGHT)
    ctx.strokeStyle = '#11221b'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }

  useEffect(paintBackground, [])

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    // Capture the pointer so a finger that slides off the box still draws, and so the
    // page does not scroll underneath the signature.
    canvasRef.current!.setPointerCapture(e.pointerId)
    drawing.current = true
    const p = pointFrom(e)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    // A tap with no drag is still a mark - some people sign with a dot and a flourish.
    ctx.lineTo(p.x + 0.1, p.y)
    ctx.stroke()
    drew.current = true
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const p = pointFrom(e)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
  }

  function end() {
    if (!drawing.current) return
    drawing.current = false
    if (!drew.current) return
    setHasMark(true)
    canvasRef.current?.toBlob((blob) => onChange(blob), 'image/png')
  }

  function clear() {
    paintBackground()
    drew.current = false
    setHasMark(false)
    onChange(null)
  }

  return (
    <div>
      <div className="spread" style={{ marginBottom: 'var(--sp-3)' }}>
        <label className="micro">{label}</label>
        {hasMark && (
          <button type="button" className="btn quiet tiny" onClick={clear} disabled={disabled}>
            Clear
          </button>
        )}
      </div>

      <canvas
        ref={canvasRef}
        width={WIDTH}
        height={HEIGHT}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          border: `1px ${hasMark ? 'solid var(--line-2)' : 'dashed var(--line-2)'}`,
          borderRadius: 'var(--r-md)',
          background: '#ffffff',
          // Without this a finger drag scrolls the drawer instead of drawing.
          touchAction: 'none',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.5 : 1,
        }}
      />

      <p className="hint" style={{ marginTop: 'var(--sp-3)' }}>
        {hasMark
          ? 'Saved with the delivery.'
          : 'Ask whoever is receiving the load to sign with their finger.'}
      </p>
    </div>
  )
}
